import React, {useCallback, useEffect, useState} from "react";
import AsyncSelect from "react-select/async";
import {apiV2} from "@/api/axiosConfig";
import {reactSelectClassNames, reactSelectStyles} from "../react-select/styling";
import {noop} from "../misc/noop";

export type CombinedFacility = {
    id: string;
    name: string;
    legacy: boolean;
};

type Option = {
    id: string;
    label: string;
    facility: CombinedFacility;
};

type CombinedFacilitySelectProps = {
    initialID?: string | null | undefined;
    clientID?: string;
    departmentID?: string;
    onChange?: (selected: CombinedFacility | undefined) => void;
    label?: string;
    required?: boolean;
};

function facilityLabel(f: CombinedFacility): string {
    return f.legacy ? `${f.name} (Legacy)` : f.name;
}

export const CombinedFacilitySelect = ({
    initialID = null,
    clientID,
    departmentID,
    onChange = noop,
    label,
    required = false,
}: CombinedFacilitySelectProps) => {
    const [selected, setSelected] = useState<Option | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!initialID || initialID === "000000000000000000000000") return;

        setIsLoading(true);
        apiV2.get<CombinedFacility>(`facilities-combined/${initialID}`)
            .then((response) => {
                const facility = response.data;
                setSelected({
                    id: facility.id,
                    label: facilityLabel(facility),
                    facility,
                });
            })
            .catch((error) => {
                console.error("Failed to load facility:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [initialID]);

    const onSelect = useCallback(
        (option: any) => {
            if (option) {
                setSelected(option);
                onChange(option.facility);
            } else {
                setSelected(null);
                onChange(undefined);
            }
        },
        [onChange]
    );

    const promiseOptions = useCallback(async (search: string) => {
        const response = await apiV2.get<CombinedFacility[]>("facilities-combined", {
            params: {
                search: search || undefined,
                clientID: clientID || undefined,
                departmentID: departmentID || undefined,
            }
        });

        return (response.data ?? []).map((f) => ({
            label: facilityLabel(f),
            value: f.id,
            facility: f,
            id: f.id,
        }));
    }, [clientID, departmentID]);

    if (isLoading) return null;

    return (
        <div className="flex flex-col relative">
            {label != undefined && (
                <label className="">
                    {label}
                    {required && <span className="text-dark-red"> *</span>}
                </label>
            )}
            <AsyncSelect
                classNames={reactSelectClassNames}
                styles={reactSelectStyles}
                placeholder="Select Facility"
                value={selected}
                onChange={onSelect}
                cacheOptions
                defaultOptions
                loadOptions={promiseOptions}
                isClearable
            />
        </div>
    );
};
